import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { useReadOnly } from '../../hooks/useReadOnly';
import { Plus, Trash2, AlertTriangle, Calendar, Clock } from 'lucide-react';
import { toast } from 'react-toastify';
import { businessToday } from '../../utils/time';
import ThemeSwitch from '../../components/ThemeSwitch';

const DAY_LABELS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function fmt12(t: string | null | undefined) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'pm' : 'am';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')}${ampm}`;
}

function formatBlockRange(b: any) {
  const end = b.end_date && b.end_date !== b.start_date ? ` – ${b.end_date}` : '';
  const time = b.start_time ? ` · ${fmt12(b.start_time)}–${fmt12(b.end_time || '')}` : ' · All day';
  return `${b.start_date}${end}${time}`;
}

export default function CrewSettings() {
  const { user } = useAuth();
  const readOnly = useReadOnly();
  const qc = useQueryClient();
  // /auth/me answers with both spellings; accept either so a refresh doesn't
  // strand the page on "Unable to load your profile".
  const memberId = (user as any)?.crewMemberId ?? (user as any)?.crew_member_id;

  const [editingAvail, setEditingAvail] = useState(false);
  const [availForm, setAvailForm] = useState<any>({});
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ start_date: '', end_date: '', start_time: '', end_time: '', note: '' });

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery(
    ['my-availability', memberId],
    () => api.get(`/availability/${memberId}`).then(r => r.data),
    { enabled: !!memberId }
  );

  const member = data?.member;
  const blocks: any[] = data?.blocks || [];
  // Austin's date, not the device's and not UTC — after 7pm local a UTC "today"
  // is tomorrow, which hid a block taken for tonight and blocked picking today.
  const today = businessToday();
  const upcoming = blocks.filter(b => (b.end_date || b.start_date) >= today);
  const past = blocks.filter(b => (b.end_date || b.start_date) < today);

  function startEditAvail() {
    setAvailForm({
      availability_days: member?.availability_days || '1,2,3,4,5',
      availability_start: member?.availability_start || '06:00',
      availability_end: member?.availability_end || '20:00',
    });
    setEditingAvail(true);
  }

  const saveAvail = useMutation(async () => {
    await api.put(`/availability/${memberId}`, availForm);
  }, {
    onSuccess: () => {
      refetch();
      qc.invalidateQueries(['my-availability', memberId]);
      setEditingAvail(false);
      toast.success('Availability updated');
    },
    onError: () => { toast.error('Failed to update availability'); },
  });

  const addBlock = useMutation(async () => {
    if (!addForm.start_date) throw new Error('Start date required');
    await api.post(`/availability/${memberId}/blocks`, {
      start_date: addForm.start_date,
      end_date: addForm.end_date || null,
      start_time: addForm.start_time || null,
      end_time: addForm.end_time || null,
      note: addForm.note || null,
    });
  }, {
    onSuccess: () => {
      refetch();
      setAddForm({ start_date: '', end_date: '', start_time: '', end_time: '', note: '' });
      setShowAdd(false);
      toast.success('Unavailability added');
    },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed'); },
  });

  const deleteBlock = useMutation(async (blockId: number) => {
    await api.delete(`/availability/${memberId}/blocks/${blockId}`);
  }, {
    onSuccess: () => { refetch(); toast.success('Block removed'); },
    onError: () => { toast.error('Failed to remove'); },
  });

  if (!memberId) {
    // An admin (or a login with no crew record) has no availability to show.
    // Say which it is instead of a bare failure message.
    return (
      <div className="text-center py-10 px-6">
        <p className="text-sm font-medium text-gray-700">No crew profile on this login</p>
        <p className="text-xs text-gray-500 mt-1">Availability belongs to a crew member. Ask the office to link this login to your crew record.</p>
      </div>
    );
  }

  if (isLoading) {
    return <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" /></div>;
  }

  if (isError) {
    return (
      <div className="text-center py-10 px-6">
        <AlertTriangle className="h-8 w-8 text-red-400 mx-auto mb-2" />
        <p className="text-sm font-medium text-gray-800">Couldn't load your availability</p>
        <p className="text-xs text-gray-500 mt-1">{(error as any)?.response?.data?.message || (error as any)?.message || 'No answer from the server.'}</p>
        <button onClick={() => refetch()} disabled={isFetching} className="btn-primary text-sm mt-4">
          {isFetching ? 'Trying…' : 'Try again'}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-6">
      <div>
        <h1 className="text-2xl font-black uppercase tracking-tight text-gray-900">My Availability</h1>
      </div>

      {/* Recurring availability */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-gray-800">Recurring Schedule</p>
          {!editingAvail && !readOnly && (
            <button onClick={startEditAvail}
              className="text-xs text-brand-600 font-medium border border-brand-200 rounded-md px-2.5 py-1">
              Edit
            </button>
          )}
        </div>

        {editingAvail ? (
          <div className="space-y-3">
            <div>
              <label className="label-sm">Available days</label>
              <div className="flex gap-1.5 flex-wrap mt-1">
                {DAY_LABELS.map((d, i) => {
                  const days = (availForm.availability_days || '').split(',').map(Number).filter((n: number) => !isNaN(n));
                  const checked = days.includes(i);
                  return (
                    <button key={d} type="button"
                      onClick={() => {
                        const updated = checked ? days.filter((x: number) => x !== i) : [...days, i];
                        setAvailForm((f: any) => ({ ...f, availability_days: updated.sort((a: number, b: number) => a - b).join(',') }));
                      }}
                      className={`w-10 h-10 rounded-full text-sm border font-medium transition-colors ${checked ? 'bg-brand-600 text-black border-brand-600' : 'bg-white text-gray-600 border-gray-300'}`}>
                      {d}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-sm">From</label>
                <input type="time" value={availForm.availability_start}
                  onChange={e => setAvailForm((f: any) => ({ ...f, availability_start: e.target.value }))}
                  className="input" />
              </div>
              <div>
                <label className="label-sm">Until</label>
                <input type="time" value={availForm.availability_end}
                  onChange={e => setAvailForm((f: any) => ({ ...f, availability_end: e.target.value }))}
                  className="input" />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setEditingAvail(false)} className="btn-secondary text-sm py-2 px-3">Cancel</button>
              <button onClick={() => saveAvail.mutate()} disabled={saveAvail.isLoading}
                className="btn-primary text-sm py-2 px-3">
                {saveAvail.isLoading ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Calendar className="h-4 w-4 text-gray-500" />
              {(member?.availability_days || '1,2,3,4,5').split(',').map(Number).map((n: number) => DAY_LABELS[n]).join(', ') || 'No days set'}
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Clock className="h-4 w-4 text-gray-500" />
              {fmt12(member?.availability_start || '06:00')} – {fmt12(member?.availability_end || '20:00')}
            </div>
          </div>
        )}
      </div>

      {/* Unavailability blocks */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-gray-800">Unavailable Dates</p>
          {!readOnly && (
            <button onClick={() => setShowAdd(v => !v)}
              className="flex items-center gap-1 text-xs text-brand-600 font-medium border border-brand-200 rounded-md px-2.5 py-1">
              <Plus className="h-3.5 w-3.5" /> Add
            </button>
          )}
        </div>

        {showAdd && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label-sm">Start date *</label>
                <input type="date" value={addForm.start_date}
                  min={today}
                  onChange={e => setAddForm(f => ({ ...f, start_date: e.target.value }))}
                  className="input" />
              </div>
              <div>
                <label className="label-sm">End date (optional)</label>
                <input type="date" value={addForm.end_date}
                  min={addForm.start_date || today}
                  onChange={e => setAddForm(f => ({ ...f, end_date: e.target.value }))}
                  className="input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label-sm">Start time (optional)</label>
                <input type="time" value={addForm.start_time}
                  onChange={e => setAddForm(f => ({ ...f, start_time: e.target.value }))}
                  className="input" />
              </div>
              <div>
                <label className="label-sm">End time</label>
                <input type="time" value={addForm.end_time}
                  onChange={e => setAddForm(f => ({ ...f, end_time: e.target.value }))}
                  className="input" />
              </div>
            </div>
            <div>
              <label className="label-sm">Reason (optional)</label>
              <input value={addForm.note}
                onChange={e => setAddForm(f => ({ ...f, note: e.target.value }))}
                className="input" placeholder="e.g. Vacation, Doctor appointment" />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowAdd(false)} className="btn-secondary text-xs py-1.5 px-3">Cancel</button>
              <button onClick={() => addBlock.mutate()} disabled={addBlock.isLoading || !addForm.start_date}
                className="btn-primary text-xs py-1.5 px-3">
                {addBlock.isLoading ? 'Adding…' : 'Add'}
              </button>
            </div>
          </div>
        )}

        {upcoming.length === 0 && !showAdd ? (
          <p className="text-sm text-gray-500 italic">No upcoming unavailability.</p>
        ) : (
          <div className="space-y-2">
            {upcoming.map((b: any) => (
              <div key={b.id} className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-red-800">{formatBlockRange(b)}</p>
                  {b.note && <p className="text-xs text-red-600 mt-0.5">{b.note}</p>}
                </div>
                {!readOnly && (
                  <button onClick={() => { if (window.confirm('Remove this unavailability?')) deleteBlock.mutate(b.id); }}
                    className="text-red-400 hover:text-red-600 flex-shrink-0 p-1">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {past.length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Past</p>
            <div className="space-y-1.5">
              {past.map((b: any) => (
                <div key={b.id} className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-1.5">
                  <span className="flex-1">{formatBlockRange(b)}{b.note ? ` · ${b.note}` : ''}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Light / Auto / Dark for this phone (owner, Sep 21 2026). */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <p className="text-sm font-semibold text-gray-800">Screen</p>
        <p className="text-xs text-gray-500 mt-0.5 mb-3">Auto goes dark when your phone does. Only changes this phone.</p>
        <ThemeSwitch />
      </div>
    </div>
  );
}
